import React, { useState, useEffect, useContext } from 'react';
import { Container, Grid, Card, CardContent, Button, Box } from '@mui/material';
import UserProfileDataForm from '../components/UserProfileDataForm';
import UserProfilePictureUploader from '../components/UserProfilePictureUpload'; 
import UserBusinessLogic from '../../../businessLogic/UserBusinessLogic';
import { IUserGuiModel, IUserProfilePictureGuiModel} from '../../../model/IUserGuiModel';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../layouts/AuthorizedLayout';

const UserProfileRoute: React.FC = () => {
    const user = useContext(UserContext);
    const [userData, setUserData] = useState<IUserGuiModel | null>(null);
    const [profilePicture, setProfilePicture] = useState<File | null>(null);
    const [currentProfilePicture, setCurrentProfilePicture] = useState<string | null>(null);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [isPictureUploaded, setIsPictureUploaded] = useState(false); 
    const userBusinessLogic = new UserBusinessLogic();
    const { t } = useTranslation();

    useEffect(() => {
        let isMounted = true;

        const fetchUserProfileData = async () => {
            try {
                if (user && user.user && user.user.id) {

                    const result = await userBusinessLogic.getProfileData(user.user.id);

                    console.log('Fetched User Data:', result.value);

                    const newUserData: IUserGuiModel = {
                        userId: { value: user.user.id }, 
                        firstName: { value: result.value.firstName || '' },
                        lastName: { value: result.value.lastName || '' },
                        mobile: { value: String(result.value.mobileNumber || '') }, 
                        phone: { value: String(result.value.phoneNumber || '') }, 
                        title: { value: result.value.title || '' }, 
                        dateOfBirth: { value: result.value.dateOfBirth || null },
                    };

                    setUserData(newUserData);

                    const pictureResult = await userBusinessLogic.getProfilePictureFile(user.user.id, 150, 150);
                    if (isMounted) {
                        if (pictureResult.value) {
                            const imageUrl = URL.createObjectURL(pictureResult.value);
                            setCurrentProfilePicture(imageUrl);
                        }
                    }
                }
            } catch (error) {
                toast.error(t("userProfile.errorFetchingProfileData"));
                console.error(t("userProfile.errorFetchingProfileData"), error);
            } finally {
                if (isMounted) setIsDataLoading(false);
            }
        };

        fetchUserProfileData();

        return () => {
            isMounted = false;
            if (currentProfilePicture) {
                URL.revokeObjectURL(currentProfilePicture);
            }
        };
    }, [user.user.id]);


    const handleSaveProfileData = async () => {
        if (!userData) return showError(t("userProfile.userDataIsMissing"));

        try {
            const response = await userBusinessLogic.putUserProfile(userData);
            if (response.hasErrors) return showError(t("userProfile.errorSavingUserProfile"), response.errors);
            toast.success(t("userProfile.profileUpdateSuccessfully"));
        } catch (error) {
            showError(t("basic.unexpectedError"));
        }
    };

    const handleSaveProfilePicture = async () => {
        if (!userData || !userData.userId) return showError(t("userProfile.userIdIsMissing"));
        if (!profilePicture) return showError(t("userProfile.profilePicIsMissing"));

        const pictureModel: IUserProfilePictureGuiModel = {
            userId: userData.userId.value,
            file: profilePicture,
        };

        try {
            const response = await userBusinessLogic.putUserProfilePicture(pictureModel);
            if (response.hasErrors) return showError(t("userProfile.errorSavingUserProfilePic"), response.errors);

            const uploadedPictureUrl = URL.createObjectURL(profilePicture);
            setCurrentProfilePicture(uploadedPictureUrl);
            setIsPictureUploaded(true);
            toast.success(t("userProfile.profilePictureUpdatedSuccessfully"));
        } catch (error) {
            showError(t("basic.unexpectedError"));
        }
    };

    const showError = (message: string, errors?: any) => {
        console.error(message, errors);
        toast.error(message);
    };

    return (
        <Container>
            <Grid container spacing={2}>
                <Grid item xs={1} sm={6}>
                    <Card>
                        <CardContent>
                            {!isDataLoading && userData ? (
                                <UserProfileDataForm
                                    initialData={userData}
                                    onDataChange={setUserData}
                                />
                            ) : (
                                <div>{t("userProfile.loading")}</div>
                            )}
                        </CardContent>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveProfileData}
                            style={{ margin: 20 }}
                        >
                            {t("userProfile.saveData")}
                        </Button>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Card>
                        <CardContent>
                            <UserProfilePictureUploader
                                onPictureChange={setProfilePicture}
                                imageUrl={currentProfilePicture}
                                isPictureUploaded={isPictureUploaded} 
                            />
                            <Box display="flex" justifyContent="center" mt={11}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveProfilePicture}
                                    style={{ marginTop: '10px' }}
                                >
                                    {t("userProfile.uploadImage")}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default UserProfileRoute;
